import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Skeleton, ConfigProvider } from 'antd';
import {
    DollarOutlined,
    ShoppingCartOutlined,
    RiseOutlined,
    BoxPlotOutlined,
    ClockCircleOutlined,
    CheckCircleOutlined,
    ExclamationCircleOutlined,
    TruckOutlined
} from '@ant-design/icons';
import { useSelector, useDispatch } from 'react-redux';
import { fetchKpiMetrics } from '../actions';
import { FILTER_TYPES } from '../constants/filterTypes';
import OrderStatusProgress from './OrderStatusProgress';
import FulfillmentChannelProgress from './FulfillmentChannelProgress';
import FinancialMetricCard from './FinancialMetricCard';
import ComparisonMetricCard from './ComparisonMetricCard';
import { orderPermissions } from '../../../utils/permissions';

const KpiDashboard = ({ onFilterChange, onApplyFilters }) => {
    const dispatch = useDispatch();
    const { filters, loading: globalLoading, kpiMetrics } = useSelector(state => state.orders);
    const { user } = useSelector(state => state.auth);
    const [localLoading, setLocalLoading] = useState(false);

    // Check permissions
    const canViewFinancialData = orderPermissions.canViewFinancialData(user);

    // Combine both global and local loading states
    const loading = globalLoading || localLoading;

    // Generate KPI data from metrics
    const generateKpiData = (metrics) => {
        if (!metrics) return [];

        const allKpiData = [
            {
                title: 'Orders',
                value: metrics.ordersCount || 0,
                color: '#1890ff',
                icon: ShoppingCartOutlined,
                description: 'Total unique orders'
            },
            {
                title: 'Sold (FBA vs FBM)',
                fbaValue: metrics.fbaMetrics?.totalQuantitySold || 0,
                fbmValue: metrics.fbmMetrics?.totalQuantitySold || 0,
                icon: ShoppingCartOutlined,
                description: 'Quantity sold',
                isComparison: true
            },
            {
                title: 'Revenue (FBA vs FBM)',
                fbaValue: metrics.fbaMetrics?.beforeTax || 0,
                fbmValue: metrics.fbmMetrics?.beforeTax || 0,
                suffix: 'USD',
                icon: RiseOutlined,
                description: 'Before tax',
                precision: 2,
                isComparison: true
            },
            {
                title: 'Earnings (FBA vs FBM)',
                fbaValue: metrics.fbaMetrics?.earnings || 0,
                fbmValue: metrics.fbmMetrics?.earnings || 0,
                suffix: 'USD',
                icon: DollarOutlined,
                description: 'From orders',
                precision: 2,
                isComparison: true
            },
            {
                title: 'Base Cost (FBA vs FBM)',
                fbaValue: metrics.fbaMetrics?.baseCosts || 0,
                fbmValue: metrics.fbmMetrics?.baseCosts || 0,
                suffix: 'USD',
                icon: ExclamationCircleOutlined,
                description: 'Costs',
                precision: 2,
                isComparison: true
            },
            {
                title: 'Profit (FBA vs FBM)',
                fbaValue: metrics.fbaMetrics?.profit || 0,
                fbmValue: metrics.fbmMetrics?.profit || 0,
                suffix: 'USD',
                icon: DollarOutlined,
                description: 'Net profit',
                precision: 2,
                isComparison: true
            },


            {
                title: 'Order Items',
                value: metrics.orderItemsCount || 0,
                color: '#722ed1',
                icon: BoxPlotOutlined,
                description: 'Total order items'
            },
            {
                title: 'Sold Total',
                value: (metrics.fbaMetrics?.totalQuantitySold || 0) + (metrics.fbmMetrics?.totalQuantitySold || 0),
                color: '#13c2c2',
                icon: ShoppingCartOutlined,
                description: 'Total quantity sold (FBA + FBM)'
            },
            {
                title: 'Revenue Total',
                value: metrics.totalBeforeTax || 0,
                suffix: 'USD',
                color: '#13c2c2',
                icon: RiseOutlined,
                description: 'Total revenue (FBA + FBM)',
                precision: 2
            },
            {
                title: 'Earnings Total',
                value: metrics.totalEarnings || 0,
                suffix: 'USD',
                color: '#52c41a',
                icon: DollarOutlined,
                description: 'Total earnings (FBA + FBM)',
                precision: 2
            },
            {
                title: 'Base Costs Total',
                value: metrics.totalBaseCosts || 0,
                suffix: 'USD',
                color: '#fa541c',
                icon: ExclamationCircleOutlined,
                description: 'Total base costs (FBA + FBM)',
                precision: 2
            },
            {
                title: 'Profit Total',
                value: metrics.profit || 0,
                suffix: 'USD',
                color: (metrics.profit || 0) >= 0 ? '#52c41a' : '#f5222d',
                icon: DollarOutlined,
                description: 'Total profit (Earnings - Base costs)',
                precision: 2
            },
            {
                title: 'Fulfillment',
                value: metrics.fulfillmentChannelStats || { AFN: { count: 0, percentage: 0 }, MFN: { count: 0, percentage: 0 }, total: 0 },
                color: '#13c2c2',
                icon: TruckOutlined,
                description: 'FBA vs FBM distribution',
                isProgress: true
            },
            {
                title: 'Ship Date Due (≤2 Days)',
                value: metrics.shipDateDueWithin2DaysCount || 0,
                color: '#fa8c16',
                icon: ClockCircleOutlined,
                description: 'Items with ship date within 2 days',
                clickable: true,
                metricType: 'shipDateDue'
            },
            {
                title: 'Delivery Date Due (≤2 Days)',
                value: metrics.deliveryDateDueWithin2DaysCount || 0,
                color: '#eb2f96',
                icon: ClockCircleOutlined,
                description: 'Items with delivery date within 2 days',
                clickable: true,
                metricType: 'deliveryDateDue'
            },
            {
                title: 'Delivery Due Today',
                value: metrics.deliveryDueTodayCount || 0,
                color: '#f5222d',
                icon: ExclamationCircleOutlined,
                description: 'Items with delivery date today',
                clickable: true,
                metricType: 'deliveryDueToday'
            },
            {
                title: 'Unfulfilled',
                value: metrics.unfulfilledCount || 0,
                color: '#faad14',
                icon: ClockCircleOutlined,
                description: 'Items need fulfillment',
                clickable: true,
                metricType: 'unfulfilled'
            },
            {
                title: 'Unconfirmed Shipments',
                value: metrics.unconfirmedShipmentCount || 0,
                color: '#f5222d',
                icon: TruckOutlined,
                description: 'Items need shipping confirmation',
                clickable: true,
                metricType: 'unconfirmedShipment'
            },
            {
                title: 'Updated Last Mile',
                value: metrics.updatedLastMileCount || 0,
                color: '#722ed1',
                icon: TruckOutlined,
                description: 'Items with updated last mile tracking',
                clickable: true,
                metricType: 'updatedLastMile'
            }
        ];

        // Filter out financial data if user doesn't have permission
        return canViewFinancialData
            ? allKpiData
            : allKpiData.filter(item => !item.isFinancial);
    };

    // Get KPI data from metrics
    const kpiData = generateKpiData(kpiMetrics);

    // Handle KPI metric click to apply corresponding filter
    const handleKpiClick = (metricType) => {
        if (!onFilterChange || !onApplyFilters) return;

        switch (metricType) {
            case 'unfulfilled':
                // Apply fulfillment status filter for unfulfilled items
                // Also exclude Shipped and Canceled orders to match KPI logic
                onFilterChange(FILTER_TYPES.FULFILLMENT_STATUS, 'Unfulfilled');
                onFilterChange(FILTER_TYPES.STATUS, ['Unshipped', 'PartiallyShipped']);
                break;
            case 'unconfirmedShipment':
                // Apply confirm shipment filter for unconfirmed items
                // Also exclude Shipped and Canceled orders to match KPI logic
                onFilterChange(FILTER_TYPES.CONFIRM_SHIPMENT, 'pending');
                onFilterChange(FILTER_TYPES.STATUS, ['Unshipped', 'PartiallyShipped']);
                break;
            case 'shipDateDue':
                // Apply ship date due filter - this will automatically add MFN filter and confirm shipment filter
                // Also exclude Shipped and Canceled orders to match KPI logic
                onFilterChange(FILTER_TYPES.SHIP_DATE_DUE, 'dayAfterTomorrow');
                onFilterChange(FILTER_TYPES.STATUS, ['Unshipped', 'PartiallyShipped']);
                break;
            case 'deliveryDateDue':
                // Apply delivery date due filter
                // Also exclude Shipped and Canceled orders to match KPI logic
                onFilterChange(FILTER_TYPES.DELIVERY_DATE_DUE, 'dayAfterTomorrow');
                // onFilterChange(FILTER_TYPES.STATUS, ['Unshipped', 'PartiallyShipped']);
                onFilterChange(FILTER_TYPES.SHIPMENT_STATUS, ['info_received', 'in_transit', 'out_for_delivery', 'failed_attempt', 'available_for_pickup', 'exception', 'pending']);
                onFilterChange(FILTER_TYPES.FULFILLMENT_CHANNEL, 'MFN');
                break;
            case 'deliveryDueToday':
                // Apply delivery date due filter for today
                // Also exclude Shipped and Canceled orders to match KPI logic
                onFilterChange(FILTER_TYPES.DELIVERY_DATE_DUE, 'today');
                onFilterChange(FILTER_TYPES.FULFILLMENT_CHANNEL, 'MFN');
                // onFilterChange(FILTER_TYPES.STATUS, ['Unshipped', 'PartiallyShipped']);
                onFilterChange(FILTER_TYPES.SHIPMENT_STATUS, ['info_received', 'in_transit', 'out_for_delivery', 'failed_attempt', 'available_for_pickup', 'exception', 'pending']);
                break;
            case 'updatedLastMile':
                // Apply updated last mile filter
                onFilterChange(FILTER_TYPES.UPDATED_LAST_MILE, 'true');
                onFilterChange(FILTER_TYPES.SHIPMENT_STATUS, ['info_received', 'in_transit', 'out_for_delivery', 'failed_attempt', 'available_for_pickup', 'exception', 'pending']);
                break;
            default:
                return;
        }

        // Apply filters after setting them
        onApplyFilters();
    };

    // // Fetch KPI data when component mounts
    // useEffect(() => {
    //     const loadKpiData = async () => {
    //         setLocalLoading(true);
    //         try {
    //             await dispatch(fetchKpiMetrics());
    //         } catch (error) {
    //             console.error('Error loading KPI data:', error);
    //         } finally {
    //             setLocalLoading(false);
    //         }
    //     };

    //     loadKpiData();
    // }, [dispatch]);

    return (
        <ConfigProvider
            theme={{
                components: {
                    Skeleton: {
                        paragraphLiHeight: 10,
                        paragraphMarginTop: 10,
                        titleHeight: 10
                    },
                },
            }}
        >
            <div>
                {loading ? (
                    <Row gutter={[8, 8]}>
                        {Array.from({ length: 10 }).map((_, index) => (
                            <Col xs={6} sm={4} md={4} lg={4} xl={4} key={index}>
                                <Card
                                    size="small"
                                    style={{
                                        background: '#fff',
                                        borderRadius: '12px',
                                        boxShadow: '2px 4px 9px -5px rgba(0,0,0,0.08)',
                                        border: '1px solid #f5f5f5',
                                        height: '72px',
                                    }}
                                    styles={{
                                        body: {
                                            padding: '5px 14px',
                                            height: '100%'
                                        }
                                    }}
                                >
                                    <Skeleton
                                        active
                                        paragraph={{ rows: 1, width: '100%' }}
                                        title={{ width: '60%' }}
                                    />
                                </Card>
                            </Col>
                        ))}
                    </Row>
                ) : (
                    <Row gutter={[8, 8]}>
                        {kpiData && kpiData.map((item, index) => {
                            const IconComponent = item.icon;

                            // Handle comparison metrics with FBA vs FBM
                            if (item.isComparison) {
                                return (
                                    <Col xs={6} sm={4} md={4} lg={4} xl={4} key={index}>
                                        <ComparisonMetricCard
                                            title={item.title}
                                            fbaValue={item.fbaValue}
                                            fbmValue={item.fbmValue}
                                            suffix={item.suffix}
                                            icon={IconComponent}
                                            description={item.description}
                                            precision={item.precision}
                                        />
                                    </Col>
                                );
                            }

                            // Handle fulfillment progress display differently
                            if (item.isProgress) {
                                const fulfillmentData = item.value;

                                return (
                                    <Col xs={6} sm={4} md={4} lg={4} xl={4} key={index}>
                                        <FulfillmentChannelProgress
                                            fulfillmentData={fulfillmentData}
                                            color={item.color}
                                        />
                                    </Col>
                                );
                            }

                            // Handle financial metrics with FBA/FBM breakdown
                            if (item.isFinancial) {
                                return (
                                    <Col xs={6} sm={4} md={4} lg={4} xl={4} key={index}>
                                        <FinancialMetricCard
                                            title={item.title}
                                            totalValue={item.value}
                                            fbaValue={item.fbaValue}
                                            fbmValue={item.fbmValue}
                                            suffix={item.suffix}
                                            color={item.color}
                                            icon={IconComponent}
                                            description={item.description}
                                            precision={item.precision}
                                        />
                                    </Col>
                                );
                            }

                            // Handle regular numeric displays
                            const displayValue = item.precision ?
                                parseFloat(item.value).toFixed(item.precision) :
                                item.value;

                            return (
                                <Col xs={6} sm={4} md={4} lg={4} xl={4} key={index}>
                                    <Card
                                        size="small"
                                        style={{
                                            background: '#fff',
                                            borderRadius: '12px',
                                            boxShadow: '2px 4px 9px -5px rgba(0,0,0,0.08)',
                                            border: '1px solid #f5f5f5',
                                            height: '72px',
                                            transition: 'box-shadow 0.2s, border 0.2s',
                                            cursor: item.clickable ? 'pointer' : 'default',
                                            ...(item.clickable && {
                                                ':hover': {
                                                    border: '1px solid #1890ff',
                                                    boxShadow: '0 4px 12px rgba(24, 144, 255, 0.15)'
                                                }
                                            })
                                        }}
                                        styles={{
                                            body: {
                                                padding: '5px 14px',
                                                height: '100%'
                                            }
                                        }}
                                        onClick={item.clickable ? () => handleKpiClick(item.metricType) : undefined}
                                        onMouseEnter={item.clickable ? (e) => {
                                            e.currentTarget.style.border = '1px solid #1890ff';
                                            e.currentTarget.style.boxShadow = '0 4px 12px rgba(24, 144, 255, 0.15)';
                                        } : undefined}
                                        onMouseLeave={item.clickable ? (e) => {
                                            e.currentTarget.style.border = '1px solid #f5f5f5';
                                            e.currentTarget.style.boxShadow = '2px 4px 9px -5px rgba(0,0,0,0.08)';
                                        } : undefined}
                                    >
                                        <div style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            marginBottom: '2px'
                                        }}>
                                            <span style={{
                                                color: item.titleColor || '#8c8c8c',
                                                fontSize: '11px',
                                                fontWeight: '500'
                                            }}>
                                                {item.title}
                                            </span>
                                            <IconComponent style={{
                                                fontSize: '12px',
                                                color: item.color
                                            }} />
                                        </div>

                                        <div style={{
                                            display: 'flex',
                                            alignItems: 'baseline',
                                            gap: '3px',
                                            marginBottom: '2px'
                                        }}>
                                            <span style={{
                                                color: item.valueColor || '#262626',
                                                fontSize: '17px',
                                                fontWeight: 'bold',
                                                lineHeight: '1.2'
                                            }}>
                                                {parseFloat(displayValue).toLocaleString()}
                                            </span>
                                            {item.suffix && (
                                                <span style={{
                                                    color: '#8c8c8c',
                                                    fontSize: '9px',
                                                    fontWeight: '400'
                                                }}>
                                                    {item.suffix}
                                                </span>
                                            )}
                                        </div>

                                        <div style={{
                                            fontSize: '11px',
                                            color: '#8c8c8c',
                                            fontWeight: '400'
                                        }}>
                                            {item.description}
                                        </div>
                                    </Card>
                                </Col>
                            );
                        })}
                    </Row>
                )}

                {/* Order Status Progress - Hidden for now to save space */}
                {/* <Row gutter={[8, 8]} style={{ marginTop: '8px' }}>
                <Col xs={24} sm={12} md={8} lg={8} xl={8}>
                    <OrderStatusProgress />
                </Col>
            </Row> */}
            </div>
        </ConfigProvider>
    );
};

export default KpiDashboard;